import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Bot, LayoutDashboard, BookOpen, MessageSquare, Settings2, Users, Sliders,
  BarChart3, RefreshCw, Save, Plus, Check, Trash2, ArrowUpRight, Search,
} from "lucide-react";
import { apiFetch } from "../../lib/api";
import { EditorialHeading, Eyebrow, GoldDivider } from "../../components/brand";

// ─────────────────────────────── shared UI ──────────────────────────────────
const TABS = [
  { key: "dashboard",     label: "Dashboard",       icon: LayoutDashboard },
  { key: "knowledge",     label: "Knowledge",       icon: BookOpen },
  { key: "prompts",       label: "Prompts",         icon: MessageSquare },
  { key: "config",        label: "AI Configuration", icon: Sliders },
  { key: "conversations", label: "Conversations",   icon: Users },
  { key: "handoffs",      label: "Human Handoff",   icon: ArrowUpRight },
  { key: "analytics",     label: "Analytics",       icon: BarChart3 },
];

function Stat({ label, value, hint, testid }) {
  return (
    <div className="rounded-lg border border-altura-gray-line bg-white p-5" data-testid={testid}>
      <div className="eyebrow text-altura-charcoal/50">{label}</div>
      <div className="mt-2 font-editorial text-3xl text-altura-charcoal">{value}</div>
      {hint ? <div className="mt-1 text-xs text-altura-charcoal/50">{hint}</div> : null}
    </div>
  );
}

function List({ items, empty = "No data yet.", testid }) {
  if (!items || items.length === 0) return <div className="text-sm text-altura-charcoal/50 italic">{empty}</div>;
  const max = Math.max(...items.map(i => i.count || 0), 1);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto] gap-3 items-center">
          <div>
            <div className="text-sm text-altura-charcoal truncate">{it.label}</div>
            <div className="mt-1 h-1 w-full bg-altura-ivory-2 rounded-full overflow-hidden">
              <div className="h-full bg-altura-gold" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
          </div>
          <div className="text-xs font-medium text-altura-charcoal/70 tabular-nums">{it.count}</div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────── DASHBOARD ──────────────────────────────────
function DashboardTab() {
  const [d, setD] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [dash, em] = await Promise.all([apiFetch("/api/auri/dashboard"), apiFetch("/api/auri/email-deliveries?limit=8")]);
      setD(dash); setEmails(em);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  if (loading || !d) return <div className="text-sm text-altura-charcoal/50">Loading dashboard…</div>;
  const c = d.counts || {};
  const mockedCount = emails.filter(x => x.status === "mocked").length;
  const sentCount = emails.filter(x => x.status === "sent").length;
  const failedCount = emails.filter(x => x.status === "failed").length;
  return (
    <div className="space-y-8" data-testid="auri-dashboard-panel">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow className="text-altura-gold-dark">AURI Executive View</Eyebrow>
          <EditorialHeading size="md" className="mt-2">AI Command Center</EditorialHeading>
        </div>
        <button onClick={load} className="btn-ghost-espresso" data-testid="auri-dash-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="AI Status" value={d.config?.is_enabled ? "Online" : "Offline"} hint={d.config?.model} testid="stat-status" />
        <Stat label="Conversations" value={c.conversations_total ?? 0} hint={`${c.conversations_active ?? 0} active`} testid="stat-convos" />
        <Stat label="Messages" value={c.messages_total ?? 0} hint={`${c.messages_24h ?? 0} in 24h · ${c.messages_7d ?? 0} in 7d`} testid="stat-msgs" />
        <Stat label="Leads Generated" value={c.leads_generated ?? 0} hint="from AURI widget" testid="stat-leads" />
        <Stat label="Handoffs" value={c.handoffs_total ?? 0} hint={`${c.handoffs_pending ?? 0} pending`} testid="stat-handoffs" />
        <Stat label="Estimated Tokens" value={(d.estimated_usage?.tokens ?? 0).toLocaleString()} hint={`≈ $${d.estimated_usage?.cost_usd_approx ?? 0}`} testid="stat-usage" />
        <Stat label="Ended" value={c.conversations_ended ?? 0} testid="stat-ended" />
        <Stat label="Assistant Replies" value={c.assistant_messages ?? 0} testid="stat-assistant" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-lg border border-altura-gray-line bg-white p-6">
          <div className="eyebrow text-altura-gold-dark mb-4">Most-Asked Questions</div>
          <List items={d.top_questions} empty="No questions yet." testid="top-questions" />
        </div>
        <div className="rounded-lg border border-altura-gray-line bg-white p-6">
          <div className="eyebrow text-altura-gold-dark mb-4">Most-Requested Countries</div>
          <List items={d.top_countries} empty="No country signals yet." testid="top-countries" />
        </div>
        <div className="rounded-lg border border-altura-gray-line bg-white p-6">
          <div className="eyebrow text-altura-gold-dark mb-4">Top Lead Sources</div>
          <List items={d.top_sources} empty="No source data yet." testid="top-sources" />
        </div>
        <div className="rounded-lg border border-altura-gray-line bg-white p-6 lg:col-span-2">
          <div className="eyebrow text-altura-gold-dark mb-4">Most-Referenced Knowledge</div>
          <List items={d.top_kb_refs} empty="No knowledge references yet." testid="top-refs" />
        </div>
        <div className="rounded-lg border border-altura-gray-line bg-white p-6">
          <div className="eyebrow text-altura-gold-dark mb-4">Unanswered / Flagged</div>
          {(!d.unanswered || d.unanswered.length === 0)
            ? <div className="text-sm text-altura-charcoal/50 italic">No flagged responses.</div>
            : <ul className="space-y-2 text-sm">{d.unanswered.map((u, i) => <li key={i} className="line-clamp-2 text-altura-charcoal/80">{u.content}</li>)}</ul>}
        </div>
      </div>

      <div className="rounded-lg border border-altura-gray-line bg-white p-6" data-testid="email-status-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow text-altura-gold-dark">Email Delivery Status</div>
            <div className="mt-1 text-sm text-altura-charcoal/60">
              {mockedCount > 0 && sentCount === 0 && failedCount === 0
                ? <span><span className="text-altura-burgundy font-medium">MOCKED</span> — Resend transport is intentionally off. Add <code className="text-xs bg-altura-ivory-2 px-1 rounded">RESEND_API_KEY</code>, <code className="text-xs bg-altura-ivory-2 px-1 rounded">SENDER_EMAIL</code>, and <code className="text-xs bg-altura-ivory-2 px-1 rounded">INTERNAL_NOTIFICATION_EMAIL</code> to the backend <code className="text-xs bg-altura-ivory-2 px-1 rounded">.env</code> to go live. No code changes needed.</span>
                : <span>{sentCount} sent · {mockedCount} mocked · {failedCount} failed (last 8)</span>}
            </div>
          </div>
        </div>
        {emails.length === 0
          ? <div className="text-sm text-altura-charcoal/50 italic">No email dispatch yet.</div>
          : <div className="divide-y divide-altura-gray-line -mx-2">
              {emails.map(e => (
                <div key={e.id} className="px-2 py-2.5 flex items-center gap-3 text-sm">
                  <span className={`inline-block h-2 w-2 rounded-full ${e.status === "sent" ? "bg-emerald-500" : e.status === "mocked" ? "bg-altura-gold" : "bg-altura-burgundy"}`} />
                  <span className="text-[10px] uppercase tracking-wider text-altura-charcoal/60 w-16">{e.status}</span>
                  <span className="text-xs text-altura-charcoal/60 w-16">{e.recipient_kind}</span>
                  <span className="flex-1 truncate text-altura-charcoal/85">{e.subject}</span>
                  <span className="text-xs text-altura-charcoal/50 truncate w-64 text-right">{e.to}</span>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}

// ─────────────────────────────── KNOWLEDGE ──────────────────────────────────
function KnowledgeTab() {
  const [sources, setSources] = useState(null);
  const [kb, setKB] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ title: "", body: "", tags: "", market: "", visibility_status: "auri_approved" });

  const load = async () => {
    const [s, k] = await Promise.all([apiFetch("/api/auri/knowledge-sources"), apiFetch("/api/auri/kb")]);
    setSources(s); setKB(k);
  };
  useEffect(() => { load(); }, []);

  const filteredKB = useMemo(() => {
    const needle = q.toLowerCase();
    return kb.filter(e => !needle || (e.title || "").toLowerCase().includes(needle) || (e.body || "").toLowerCase().includes(needle));
  }, [kb, q]);

  const saveNew = async () => {
    if (!draft.title.trim() || !draft.body.trim()) { toast.error("Title and body required"); return; }
    await apiFetch("/api/auri/kb", { method: "POST", body: {
      title: draft.title, body: draft.body,
      tags: draft.tags ? draft.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      market: draft.market || null,
      visibility_status: draft.visibility_status,
    }});
    setShowNew(false);
    setDraft({ title: "", body: "", tags: "", market: "", visibility_status: "auri_approved" });
    toast.success("KB entry created");
    load();
  };

  const toggleActive = async (e) => {
    await apiFetch(`/api/auri/kb/${e.id}`, { method: "PATCH", body: { is_active: !e.is_active }});
    load();
  };

  const remove = async (e) => {
    if (!window.confirm("Delete this KB entry?")) return;
    await apiFetch(`/api/auri/kb/${e.id}`, { method: "DELETE" });
    load();
  };

  return (
    <div className="space-y-8" data-testid="auri-knowledge-panel">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow className="text-altura-gold-dark">AURI Knowledge</Eyebrow>
          <EditorialHeading size="md" className="mt-2">Knowledge Management</EditorialHeading>
          <p className="mt-3 text-sm text-altura-charcoal/70 max-w-2xl">AURI reads only content marked <b>auri_approved</b> or <b>public_approved</b>. Internal notes, drafts, archived, and confidential partnership data are never surfaced.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-gold" data-testid="auri-kb-new"><Plus className="h-4 w-4" /> New Entry</button>
      </div>

      {sources && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ["Articles", sources.articles?.count],
            ["Resources", sources.resources?.count],
            ["Country Intel", sources.country_intel?.count],
            ["Opportunities", sources.opportunities?.count],
            ["Partners", sources.partners?.count],
            ["Native KB", sources.kb?.count],
          ].map(([lbl, c]) => (
            <div key={lbl} className="rounded-lg border border-altura-gray-line bg-white p-4">
              <div className="eyebrow text-altura-charcoal/50">{lbl}</div>
              <div className="mt-2 font-editorial text-2xl text-altura-charcoal">{c ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-altura-charcoal/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search KB entries…" className="w-full pl-9 pr-4 py-2.5 rounded-md border border-altura-gray-line bg-white text-sm" data-testid="kb-search" />
        </div>
        <div className="mt-4 space-y-3">
          {filteredKB.length === 0 && <div className="text-sm text-altura-charcoal/50 italic">No KB entries.</div>}
          {filteredKB.map(e => (
            <div key={e.id} className="rounded-lg border border-altura-gray-line bg-white p-4" data-testid={`kb-item-${e.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-editorial text-base text-altura-charcoal">{e.title}</div>
                    <span className={`badge-soft ${e.is_active ? "" : "opacity-50"}`}>{e.visibility_status}</span>
                    {e.market ? <span className="badge-soft">{e.market}</span> : null}
                    {!e.is_active ? <span className="badge-soft text-altura-burgundy">inactive</span> : null}
                  </div>
                  <div className="mt-2 text-sm text-altura-charcoal/70 line-clamp-2">{e.body}</div>
                  {e.tags?.length ? <div className="mt-2 flex flex-wrap gap-1.5">{e.tags.map(t => <span key={t} className="text-[10px] uppercase tracking-wider text-altura-gold-dark border border-altura-gold/40 rounded-full px-2 py-0.5">{t}</span>)}</div> : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => toggleActive(e)} className="text-xs px-2 py-1 rounded border border-altura-gray-line hover:border-altura-gold" data-testid={`kb-toggle-${e.id}`}>{e.is_active ? "Disable" : "Enable"}</button>
                  <button onClick={() => remove(e)} className="text-xs px-2 py-1 rounded border border-altura-burgundy/30 text-altura-burgundy hover:border-altura-burgundy" data-testid={`kb-delete-${e.id}`}><Trash2 className="h-3 w-3 inline" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <EditorialHeading size="sm">New Knowledge Entry</EditorialHeading>
            <input value={draft.title} onChange={e => setDraft({...draft, title: e.target.value})} placeholder="Title" className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="kb-new-title" />
            <textarea value={draft.body} onChange={e => setDraft({...draft, body: e.target.value})} placeholder="Body — the exact content AURI may cite from" rows={7} className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="kb-new-body" />
            <div className="grid grid-cols-3 gap-3">
              <input value={draft.tags} onChange={e => setDraft({...draft, tags: e.target.value})} placeholder="tags,comma,separated" className="px-3 py-2 border border-altura-gray-line rounded-md text-sm" />
              <select value={draft.market} onChange={e => setDraft({...draft, market: e.target.value})} className="px-3 py-2 border border-altura-gray-line rounded-md text-sm">
                <option value="">Any market</option>
                {["ghana","kenya","nigeria","tanzania","dubai","punta_cana"].map(m => <option key={m}>{m}</option>)}
              </select>
              <select value={draft.visibility_status} onChange={e => setDraft({...draft, visibility_status: e.target.value})} className="px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="kb-new-visibility">
                <option value="auri_approved">auri_approved</option>
                <option value="public_approved">public_approved</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="btn-ghost-espresso">Cancel</button>
              <button onClick={saveNew} className="btn-gold" data-testid="kb-new-save"><Save className="h-4 w-4" /> Save Entry</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── PROMPTS ────────────────────────────────────
function PromptsTab() {
  const [prompts, setPrompts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ name: "", system_prompt: "", persona: "", refusal_template: "", notes: "" });

  const load = async () => {
    const p = await apiFetch("/api/auri/prompts");
    setPrompts(p);
    if (selected) {
      const refreshed = p.find(x => x.id === selected.id);
      setSelected(refreshed || (p.length ? p[0] : null));
    } else if (p.length) {
      setSelected(p[0]);
    }
  };
  useEffect(() => { load(); }, []);

  const savePrompt = async () => {
    if (!draft.name.trim() || !draft.system_prompt.trim()) { toast.error("Name and system prompt required"); return; }
    await apiFetch("/api/auri/prompts", { method: "POST", body: draft });
    toast.success("Prompt saved");
    setShowNew(false);
    setDraft({ name: "", system_prompt: "", persona: "", refusal_template: "", notes: "" });
    load();
  };
  const activate = async (p) => { await apiFetch(`/api/auri/prompts/${p.id}/activate`, { method: "POST" }); toast.success("Activated"); load(); };
  const savePatch = async (patch) => {
    await apiFetch(`/api/auri/prompts/${selected.id}`, { method: "PATCH", body: patch });
    toast.success("Updated");
    load();
  };

  return (
    <div className="space-y-6" data-testid="auri-prompts-panel">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow className="text-altura-gold-dark">Prompt Management</Eyebrow>
          <EditorialHeading size="md" className="mt-2">System Prompt & Persona</EditorialHeading>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-gold" data-testid="auri-prompt-new"><Plus className="h-4 w-4" /> New Version</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-2">
          {prompts.map(p => (
            <button key={p.id} onClick={() => setSelected(p)} className={`w-full text-left p-3 rounded-md border ${selected?.id === p.id ? "border-altura-gold bg-altura-ivory-2" : "border-altura-gray-line bg-white"}`} data-testid={`prompt-item-${p.id}`}>
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-altura-charcoal truncate">{p.name} <span className="text-altura-charcoal/40">v{p.version}</span></div>
                {p.is_active && <span className="ml-auto text-[10px] uppercase tracking-wider text-altura-gold-dark border border-altura-gold rounded-full px-2">Active</span>}
              </div>
              <div className="text-xs text-altura-charcoal/50 mt-1 line-clamp-2">{p.persona || p.system_prompt?.slice(0, 80)}</div>
            </button>
          ))}
        </div>
        {selected && (
          <div className="rounded-lg border border-altura-gray-line bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-editorial text-xl">{selected.name} <span className="text-altura-charcoal/40 text-sm">v{selected.version}</span></div>
                <div className="text-xs text-altura-charcoal/50">{selected.is_active ? "This prompt is currently active." : "Inactive draft version."}</div>
              </div>
              {!selected.is_active && <button onClick={() => activate(selected)} className="btn-outline-gold" data-testid="prompt-activate"><Check className="h-4 w-4" /> Activate</button>}
            </div>
            <div>
              <label className="eyebrow text-altura-charcoal/60">Persona</label>
              <textarea defaultValue={selected.persona || ""} onBlur={(e) => savePatch({ persona: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" />
            </div>
            <div>
              <label className="eyebrow text-altura-charcoal/60">System Prompt</label>
              <textarea defaultValue={selected.system_prompt || ""} onBlur={(e) => savePatch({ system_prompt: e.target.value })} rows={16} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-xs font-mono" data-testid="prompt-system" />
            </div>
            <div>
              <label className="eyebrow text-altura-charcoal/60">Refusal Template</label>
              <textarea defaultValue={selected.refusal_template || ""} onBlur={(e) => savePatch({ refusal_template: e.target.value })} rows={3} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" />
            </div>
          </div>
        )}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 space-y-3" onClick={e => e.stopPropagation()}>
            <EditorialHeading size="sm">New Prompt Version</EditorialHeading>
            <input value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})} placeholder="Name (e.g. Altura Concierge)" className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="prompt-new-name" />
            <textarea value={draft.persona} onChange={e => setDraft({...draft, persona: e.target.value})} placeholder="Persona summary" rows={2} className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" />
            <textarea value={draft.system_prompt} onChange={e => setDraft({...draft, system_prompt: e.target.value})} placeholder="System prompt (persona, tone, safety rules, next-step behaviour)" rows={12} className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-xs font-mono" data-testid="prompt-new-system" />
            <textarea value={draft.refusal_template} onChange={e => setDraft({...draft, refusal_template: e.target.value})} placeholder="Refusal template" rows={3} className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="btn-ghost-espresso">Cancel</button>
              <button onClick={savePrompt} className="btn-gold" data-testid="prompt-new-save"><Save className="h-4 w-4" /> Save Draft</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── CONFIG ─────────────────────────────────────
function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  const load = async () => setCfg(await apiFetch("/api/auri/config"));
  useEffect(() => { load(); }, []);
  if (!cfg) return <div>Loading…</div>;

  const patch = async (body) => {
    const next = await apiFetch("/api/auri/config", { method: "PATCH", body });
    setCfg(next);
    toast.success("Configuration saved");
  };

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const MODEL_OPTIONS = [
    { provider: "anthropic", model: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (recommended)" },
    { provider: "anthropic", model: "claude-sonnet-4-6",           label: "Claude Sonnet 4.6 (future)" },
    { provider: "anthropic", model: "claude-opus-4-8",             label: "Claude Opus 4.8 (future)" },
    { provider: "openai",    model: "gpt-5.4",                     label: "GPT-5.4 (future)" },
    { provider: "gemini",    model: "gemini-3.1-pro-preview",      label: "Gemini 3.1 Pro (future)" },
  ];

  return (
    <div className="space-y-6" data-testid="auri-config-panel">
      <div>
        <Eyebrow className="text-altura-gold-dark">AI Configuration</Eyebrow>
        <EditorialHeading size="md" className="mt-2">Model & Behaviour</EditorialHeading>
      </div>

      <div className="rounded-lg border border-altura-gray-line bg-white p-6 space-y-5 max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-editorial text-lg">AURI Enabled</div>
            <div className="text-sm text-altura-charcoal/60">Turn the public widget on or off.</div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!cfg.is_enabled} onChange={e => set("is_enabled", e.target.checked)} data-testid="config-enabled" />
            <span className="text-sm">{cfg.is_enabled ? "Online" : "Offline"}</span>
          </label>
        </div>

        <div>
          <label className="eyebrow text-altura-charcoal/60">Model</label>
          <select value={`${cfg.provider}:${cfg.model}`} onChange={e => {
            const [provider, model] = e.target.value.split(":");
            set("provider", provider); set("model", model);
          }} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="config-model">
            {MODEL_OPTIONS.map(o => <option key={o.model} value={`${o.provider}:${o.model}`}>{o.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="eyebrow text-altura-charcoal/60">Temperature ({cfg.temperature})</label>
            <input type="range" min="0" max="1" step="0.05" value={cfg.temperature} onChange={e => set("temperature", parseFloat(e.target.value))} className="w-full mt-1" data-testid="config-temperature" />
            <div className="text-xs text-altura-charcoal/50">Lower = more precise. Higher = more creative.</div>
          </div>
          <div>
            <label className="eyebrow text-altura-charcoal/60">Max Tokens (response length)</label>
            <input type="number" min="200" max="4000" step="100" value={cfg.max_tokens} onChange={e => set("max_tokens", parseInt(e.target.value || "0"))} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="config-max-tokens" />
          </div>
          <div>
            <label className="eyebrow text-altura-charcoal/60">Rate limit / hour (per visitor)</label>
            <input type="number" min="5" max="200" value={cfg.rate_limit_per_hour} onChange={e => set("rate_limit_per_hour", parseInt(e.target.value || "0"))} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="config-rl-hour" />
          </div>
          <div>
            <label className="eyebrow text-altura-charcoal/60">Rate limit / day (per visitor)</label>
            <input type="number" min="20" max="1000" value={cfg.rate_limit_per_day} onChange={e => set("rate_limit_per_day", parseInt(e.target.value || "0"))} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="config-rl-day" />
          </div>
        </div>

        <div>
          <label className="eyebrow text-altura-charcoal/60">Widget Welcome Message</label>
          <textarea value={cfg.welcome_message} onChange={e => set("welcome_message", e.target.value)} rows={4} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="config-welcome" />
        </div>

        <div>
          <label className="eyebrow text-altura-charcoal/60">Handoff Trigger Keywords</label>
          <input value={(cfg.handoff_threshold_keywords || []).join(", ")} onChange={e => set("handoff_threshold_keywords", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="w-full mt-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="config-handoff-keywords" />
          <div className="text-xs text-altura-charcoal/50 mt-1">Comma-separated. Match triggers an automatic Human Handoff.</div>
        </div>

        <div className="flex justify-end pt-2 border-t border-altura-gray-line">
          <button onClick={() => patch({
            is_enabled: cfg.is_enabled, provider: cfg.provider, model: cfg.model,
            temperature: cfg.temperature, max_tokens: cfg.max_tokens,
            welcome_message: cfg.welcome_message,
            handoff_threshold_keywords: cfg.handoff_threshold_keywords,
            rate_limit_per_hour: cfg.rate_limit_per_hour, rate_limit_per_day: cfg.rate_limit_per_day,
          })} className="btn-gold" data-testid="config-save"><Save className="h-4 w-4" /> Save Configuration</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── CONVERSATIONS ──────────────────────────────
function ConversationsTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => setItems(await apiFetch(`/api/auri/conversations${q ? `?q=${encodeURIComponent(q)}` : ""}`));
  useEffect(() => { load(); }, [q]);

  const open = async (c) => setSelected(await apiFetch(`/api/auri/conversations/${c.id}`));
  const end = async (c) => { await apiFetch(`/api/auri/conversations/${c.id}/end`, { method: "POST" }); toast.success("Conversation ended"); load(); };

  return (
    <div className="space-y-4" data-testid="auri-conversations-panel">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow className="text-altura-gold-dark">Conversation History</Eyebrow>
          <EditorialHeading size="md" className="mt-2">Visitor Sessions</EditorialHeading>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, session…" className="px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="convo-search" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
          {items.length === 0 && <div className="text-sm text-altura-charcoal/50 italic">No conversations yet.</div>}
          {items.map(c => (
            <button key={c.id} onClick={() => open(c)} className={`w-full text-left p-3 rounded-md border ${selected?.id === c.id ? "border-altura-gold bg-altura-ivory-2" : "border-altura-gray-line bg-white"}`} data-testid={`convo-item-${c.id}`}>
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-altura-charcoal truncate">{c.visitor_name || c.visitor_email || c.session_id?.slice(0, 8)}</div>
                <span className={`ml-auto text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${c.status === "handoff" ? "border-altura-burgundy text-altura-burgundy" : c.status === "ended" ? "border-altura-charcoal/30 text-altura-charcoal/50" : "border-altura-gold text-altura-gold-dark"}`}>{c.status}</span>
              </div>
              <div className="text-xs text-altura-charcoal/50 mt-1">{c.message_count || 0} msgs · {c.source_page || "unknown page"}</div>
              <div className="text-xs text-altura-charcoal/40 mt-1">{c.last_message_at?.slice(0, 16)?.replace("T", " ")}</div>
            </button>
          ))}
        </div>
        {selected ? (
          <div className="rounded-lg border border-altura-gray-line bg-white p-6 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-editorial text-lg">{selected.visitor_name || selected.visitor_email || selected.session_id}</div>
                <div className="text-xs text-altura-charcoal/50">Source: {selected.source_page || "unknown"} · Status: {selected.status}</div>
              </div>
              {selected.status !== "ended" && <button onClick={() => end(selected)} className="btn-ghost-espresso" data-testid="convo-end">End</button>}
            </div>
            <div className="space-y-3">
              {(selected.messages || []).map(m => (
                <div key={m.id} className={`p-3 rounded-md ${m.role === "assistant" ? "bg-altura-ivory-2" : "bg-white border border-altura-gray-line"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-altura-charcoal/50">{m.role} · {m.created_at?.slice(11, 16)}</div>
                  <div className="mt-1 text-sm text-altura-charcoal whitespace-pre-wrap">{m.content}</div>
                  {m.kb_refs?.length > 0 && <div className="mt-2 text-[10px] text-altura-gold-dark">refs: {m.kb_refs.join(", ")}</div>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-altura-gray-line bg-white p-6 text-sm text-altura-charcoal/50 italic">Select a conversation to view messages.</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────── HANDOFFS ───────────────────────────────────
function HandoffsTab() {
  const [items, setItems] = useState([]);
  const load = async () => setItems(await apiFetch("/api/auri/handoffs"));
  useEffect(() => { load(); }, []);
  const update = async (h, status, resolution_note) => {
    await apiFetch(`/api/auri/handoffs/${h.id}`, { method: "PATCH", body: { status, resolution_note }});
    toast.success(`Handoff ${status}`);
    load();
  };
  return (
    <div className="space-y-4" data-testid="auri-handoffs-panel">
      <div>
        <Eyebrow className="text-altura-gold-dark">Human Handoff</Eyebrow>
        <EditorialHeading size="md" className="mt-2">Advisor Queue</EditorialHeading>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-altura-charcoal/50 italic">No handoffs yet.</div>}
        {items.map(h => (
          <div key={h.id} className="rounded-lg border border-altura-gray-line bg-white p-4" data-testid={`handoff-${h.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-editorial text-base">{h.visitor_name || h.visitor_email || "Anonymous visitor"}</div>
                  <span className="badge-soft">{h.trigger}</span>
                  <span className={`badge-soft ${h.status === "pending" ? "text-altura-gold-dark" : h.status === "resolved" ? "text-altura-charcoal/50" : ""}`}>{h.status}</span>
                  {h.lead_id ? <span className="text-[10px] text-altura-gold-dark border border-altura-gold/40 rounded-full px-2 py-0.5">Lead #{h.lead_id.slice(-6)}</span> : null}
                </div>
                {h.visitor_email && <div className="text-sm text-altura-charcoal/70 mt-1">{h.visitor_email}</div>}
                <div className="text-sm text-altura-charcoal/70 mt-1">{h.reason}</div>
                <div className="text-xs text-altura-charcoal/40 mt-1">{h.created_at?.slice(0, 16)?.replace("T", " ")} · session {h.session_id?.slice(0, 8)}</div>
                {h.resolution_note && <div className="mt-2 text-xs text-altura-charcoal/60 italic">Note: {h.resolution_note}</div>}
              </div>
              {h.status === "pending" && (
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => update(h, "acknowledged")} className="text-xs px-3 py-1.5 rounded border border-altura-gold text-altura-gold-dark hover:bg-altura-gold/10">Acknowledge</button>
                  <button onClick={() => {
                    const note = window.prompt("Resolution note?");
                    update(h, "resolved", note || "");
                  }} className="text-xs px-3 py-1.5 rounded border border-altura-charcoal/20 hover:bg-altura-ivory-2">Resolve</button>
                  <button onClick={() => update(h, "dismissed")} className="text-xs px-3 py-1.5 rounded border border-altura-burgundy/30 text-altura-burgundy hover:bg-altura-burgundy/5">Dismiss</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────── ANALYTICS ──────────────────────────────────
function AnalyticsTab() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const load = async () => setD(await apiFetch(`/api/auri/analytics?days=${days}`));
  useEffect(() => { load(); }, [days]);
  if (!d) return <div>Loading…</div>;
  const t = d.totals || {};
  const max = Math.max(1, ...(d.series || []).map(s => (s.user || 0) + (s.assistant || 0)));
  return (
    <div className="space-y-6" data-testid="auri-analytics-panel">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow className="text-altura-gold-dark">Analytics</Eyebrow>
          <EditorialHeading size="md" className="mt-2">Executive Report</EditorialHeading>
        </div>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} className="px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="analytics-range">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Messages" value={t.messages ?? 0} />
        <Stat label="Conversations" value={t.conversations ?? 0} />
        <Stat label="Handoffs" value={t.handoffs ?? 0} />
        <Stat label="Leads" value={t.leads ?? 0} />
        <Stat label="Lead Conv. Rate" value={`${t.lead_conversion_rate_pct ?? 0}%`} />
        <Stat label="Handoff Rate" value={`${t.handoff_rate_pct ?? 0}%`} />
      </div>
      <div className="rounded-lg border border-altura-gray-line bg-white p-6">
        <div className="eyebrow text-altura-gold-dark mb-4">Daily Volume</div>
        <div className="flex items-end gap-1.5 h-40">
          {(d.series || []).map(s => {
            const h = ((s.user || 0) + (s.assistant || 0)) / max * 100;
            return (
              <div key={s.date} className="flex-1 flex flex-col items-center gap-1" title={`${s.date}: ${(s.user||0)+(s.assistant||0)} msgs`}>
                <div className="w-full bg-altura-gold/20 relative rounded-t-sm" style={{ height: `${h}%` }}>
                  <div className="absolute inset-x-0 bottom-0 bg-altura-gold rounded-t-sm" style={{ height: `${((s.assistant||0)/((s.user||0)+(s.assistant||0)||1))*100}%` }} />
                </div>
                <div className="text-[9px] text-altura-charcoal/40 rotate-45 origin-left">{s.date?.slice(5)}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-altura-charcoal/60">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 bg-altura-gold rounded-sm" /> Assistant</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 bg-altura-gold/20 rounded-sm" /> User</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── MAIN PAGE ──────────────────────────────────
export default function AuriCommand() {
  const [tab, setTab] = useState("dashboard");
  const Comp = { dashboard: DashboardTab, knowledge: KnowledgeTab, prompts: PromptsTab, config: ConfigTab, conversations: ConversationsTab, handoffs: HandoffsTab, analytics: AnalyticsTab }[tab];
  return (
    <div className="min-h-screen" data-testid="auri-command">
      <div className="border-b border-altura-gray-line bg-white px-8 py-5">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-altura-gold/10 text-altura-gold-dark"><Bot className="h-4 w-4" /></div>
          <div>
            <div className="eyebrow text-altura-gold-dark">Altura Command Center</div>
            <div className="font-editorial text-2xl text-altura-charcoal leading-tight">AURI · AI Operations</div>
          </div>
        </div>
      </div>
      <div className="border-b border-altura-gray-line bg-white sticky top-0 z-10">
        <div className="px-8 flex flex-wrap gap-1 -mb-px">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 ${tab === t.key ? "border-altura-gold text-altura-charcoal" : "border-transparent text-altura-charcoal/60 hover:text-altura-charcoal"}`}
                    data-testid={`auri-tab-${t.key}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-8">
        <Comp />
      </div>
    </div>
  );
}
